import React, { useState, useEffect, useCallback } from 'react';
import { Hash, CheckCircle, XCircle, ChevronDown, ChevronUp, RefreshCw, Search, X } from 'lucide-react';
import { vendorCodeConflictService, getErrorMessage } from '../../services/apiService';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';
import SyncStatusPanel from '../../components/erp-sync/SyncStatusPanel';
import { useErpSync } from '../../hooks/useErpSync';
import DuplicateVendorList from './components/DuplicateVendorList';
import AssignErpCodeModal from './components/AssignErpCodeModal';

const norm = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
const normPhone = (s) => ((s || '').replace(/\D/g, '')).slice(-10);

// Values (long emails especially) must wrap inside the candidate card rather than
// spilling past its border: min-w-0 lets the flex child shrink, break-all wraps it.
const FieldRow = ({ label, candidateValue, matches }) => (
  <div className="flex items-start justify-between gap-2 text-sm py-0.5">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span
      className={`min-w-0 text-right break-all ${
        matches ? 'text-green-700 font-medium' : 'text-gray-800'
      }`}
    >
      {candidateValue || '—'}
      {matches && <CheckCircle className="inline ml-1 h-3.5 w-3.5 text-green-600" />}
    </span>
  </div>
);

const VendorCodeConflicts = () => {
  const [tab, setTab] = useState('conflicts'); // 'conflicts' | 'duplicates'

  const [groups, setGroups] = useState([]);    // conflicts grouped by company
  const [total, setTotal] = useState(0);       // companies (the unit of pagination)
  const [rowTotal, setRowTotal] = useState(0); // individual conflict rows
  const [duplicates, setDuplicates] = useState([]);   // grouped by company
  const [dupTotal, setDupTotal] = useState(0);        // companies
  const [dupRowTotal, setDupRowTotal] = useState(0);  // individual duplicate rows
  const [assignTarget, setAssignTarget] = useState(null);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [expandedKey, setExpandedKey] = useState(null); // company_key
  const [selected, setSelected] = useState({});         // conflictId -> string[] of codes
  const [confirm, setConfirm] = useState(null);         // {type, conflict}
  const [busy, setBusy] = useState(false);
  const [syncConfirm, setSyncConfirm] = useState(false);
  const { success, error } = useToast();
  const limit = 25;

  const toggleCode = (conflictId, code) =>
    setSelected((s) => {
      const cur = new Set(s[conflictId] || []);
      if (cur.has(code)) cur.delete(code); else cur.add(code);
      return { ...s, [conflictId]: [...cur] };
    });

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch BOTH tabs together so each tab's count badge is accurate regardless of which
  // tab is currently active (otherwise the inactive tab's badge stays at its initial 0
  // until you open it). Both datasets are small — the service loads and groups the full
  // set in memory either way — and this also makes switching tabs instant.
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [conflictRes, dupRes] = await Promise.all([
        vendorCodeConflictService.getConflicts(page, limit, search),
        vendorCodeConflictService.getDuplicateVendors(page, limit, search),
      ]);
      setGroups(conflictRes.data || []);
      setTotal(conflictRes.pagination?.total ?? 0);
      setRowTotal(conflictRes.pagination?.rowTotal ?? 0);
      setDuplicates(dupRes.data || []);
      setDupTotal(dupRes.pagination?.total ?? 0);
      setDupRowTotal(dupRes.pagination?.rowTotal ?? 0);
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, search, error]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSyncComplete = useCallback(
    (lastResult) => {
      if (lastResult) {
        const dupes = lastResult.duplicateCount ? `, ${lastResult.duplicateCount} duplicates` : '';
        success(
          `Sync completed: ${lastResult.assignedCount} assigned, ${lastResult.conflictCount} conflicts${dupes}, ${lastResult.unmatched} unmatched, ${lastResult.errors} errors.`
        );
      }
      setPage(1);
      fetchData();
    },
    [success, fetchData]
  );

  const sync = useErpSync({ onComplete: handleSyncComplete });

  const runSyncERP = async () => {
    setSyncConfirm(false);
    const errMsg = await sync.start();
    if (errMsg) error(errMsg);
    else success('ERP sync started — progress is shown in the panel.');
  };

  const handleResolve = async () => {
    const { conflict } = confirm;
    const codes = selected[conflict.id] || [];
    setBusy(true);
    try {
      await vendorCodeConflictService.resolve(conflict.id, codes);
      success(`Code${codes.length > 1 ? 's' : ''} ${codes.join(', ')} assigned to ${conflict.vendor.company_name}`);
      setConfirm(null);
      fetchData();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    const { conflict } = confirm;
    setBusy(true);
    try {
      await vendorCodeConflictService.dismiss(conflict.id);
      success('Conflict dismissed — vendor stays without a code');
      setConfirm(null);
      fetchData();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const activeTotal = tab === 'conflicts' ? total : dupTotal;
  const totalPages = Math.max(1, Math.ceil(activeTotal / limit));

  const TabButton = ({ id, label, count }) => (
    <button
      onClick={() => { setTab(id); setPage(1); setExpandedKey(null); }}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
        tab === id
          ? 'border-amber-500 text-amber-700'
          : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
      }`}
    >
      {label}
      {count != null && (
        <span
          className={`ml-2 text-xs rounded-full px-2 py-0.5 ${
            tab === id ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Hash className="h-7 w-7 text-amber-600" /> Vendor Codes
          </h1>
          <p className="text-gray-600 mt-1">
            Vendors the ERP sync could not code on its own — ambiguous matches that need a
            decision, and duplicate rows that need cleaning up.
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading} className="shrink-0">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div>
          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
            <TabButton id="conflicts" label="Code conflicts" count={total} />
            <TabButton id="duplicates" label="Duplicate vendors" count={dupTotal} />
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by company name or email…"
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {tab === 'duplicates' ? (
            <DuplicateVendorList
              groups={duplicates}
              rowTotal={dupRowTotal}
              total={dupTotal}
              loading={loading}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              onAssign={setAssignTarget}
            />
          ) : loading && groups.length === 0 ? (
            <div className="text-center py-16 text-gray-500">Loading…</div>
          ) : groups.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
              <p className="text-gray-700 font-medium">
                {search ? 'No conflicts match that search' : 'No pending code conflicts'}
              </p>
              <p className="text-gray-500 text-sm">
                Ambiguous ERP matches will appear here after each sync.
              </p>
            </div>
          ) : (
            <>
              {rowTotal > total && (
                <p className="text-xs text-gray-500 mb-2">
                  {rowTotal} conflict rows across {total} companies — rows for the same company
                  are grouped together.
                </p>
              )}
              <div className="space-y-3">
                {groups.map((group) => {
                  const isOpen = expandedKey === group.company_key;
                  const rows = group.conflicts || [];
                  return (
                    <div key={group.company_key} className="bg-white rounded-lg border shadow-sm">
                      <button
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                        onClick={() => setExpandedKey(isOpen ? null : group.company_key)}
                      >
                        <div className="min-w-0">
                          <span className="font-semibold text-gray-900">{group.company_name}</span>
                          {rows.length === 1 && (
                            <span className="ml-3 text-sm text-gray-500 break-all">
                              {rows[0].vendor.email}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {rows.length > 1 && (
                            <span className="text-sm bg-slate-100 text-slate-700 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                              {rows.length} vendor rows
                            </span>
                          )}
                          <span className="text-sm bg-amber-100 text-amber-800 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                            {rows.length === 1
                              ? `${(rows[0].candidates || []).length} candidates`
                              : 'needs decisions'}
                          </span>
                          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </button>

                      {isOpen && (
                        <div className="border-t">
                          {rows.map((conflict, idx) => {
                            const v = conflict.vendor;
                            const candidates = Array.isArray(conflict.candidates) ? conflict.candidates : [];
                            return (
                              <div
                                key={conflict.id}
                                className={`px-4 py-4 ${idx > 0 ? 'border-t border-dashed' : ''}`}
                              >
                                {rows.length > 1 && (
                                  <p className="text-xs text-gray-500 mb-2">
                                    Vendor <span className="font-medium text-gray-700">#{v.id}</span>
                                    {' · '}
                                    <span className="break-all">{v.email}</span>
                                  </p>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                  {candidates.map((c) => {
                                    const isSelected = (selected[conflict.id] || []).includes(c.code);
                                    return (
                                      <label
                                        key={c.code}
                                        className={`block min-w-0 rounded-lg border p-3 cursor-pointer transition ${
                                          isSelected
                                            ? 'border-amber-500 ring-2 ring-amber-200'
                                            : 'hover:border-gray-400'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="font-mono font-bold text-gray-900">{c.code}</span>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleCode(conflict.id, c.code)}
                                          />
                                        </div>
                                        <FieldRow label="Name" candidateValue={c.name} matches={norm(c.name) === norm(v.company_name)} />
                                        <FieldRow label="Email" candidateValue={c.email} matches={!!c.email && norm(c.email) === norm(v.email)} />
                                        <FieldRow label="Phone" candidateValue={c.phone} matches={!!c.phone && normPhone(c.phone) === normPhone(v.phone_number)} />
                                        <FieldRow label="Group" candidateValue={c.group} matches={!!c.group && norm(c.group) === norm(v.supplier_group)} />
                                        <FieldRow label="Entity" candidateValue={c.dataAreaId} matches={false} />
                                        <FieldRow label="State" candidateValue={c.state} matches={false} />
                                      </label>
                                    );
                                  })}
                                </div>
                                <div className="flex items-center justify-end gap-2 mt-4">
                                  <Button variant="outline" onClick={() => setConfirm({ type: 'dismiss', conflict })}>
                                    <XCircle className="h-4 w-4 mr-1" /> None of these
                                  </Button>
                                  <Button
                                    disabled={(selected[conflict.id] || []).length === 0}
                                    onClick={() => setConfirm({ type: 'resolve', conflict })}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" /> Assign selected ({(selected[conflict.id] || []).length})
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6">
                  <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                  <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                  <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              )}
            </>
          )}
        </div>

        <aside className="lg:sticky lg:top-6">
          <SyncStatusPanel sync={sync} onStart={() => setSyncConfirm(true)} />
        </aside>
      </div>

      {/* ConfirmDialog uses isOpen/onClose/onConfirm/confirmText/cancelText/variant/loading — no onCancel */}
      {confirm && (
        <ConfirmDialog
          isOpen={true}
          title={confirm.type === 'resolve' ? 'Assign vendor code?' : 'Dismiss conflict?'}
          message={
            confirm.type === 'resolve'
              ? `Assign code${(selected[confirm.conflict.id] || []).length > 1 ? 's' : ''} ${(selected[confirm.conflict.id] || []).join(', ')} to ${confirm.conflict.vendor.company_name}? The vendor will be notified by email.`
              : `Dismiss this conflict? ${confirm.conflict.vendor.company_name} keeps no vendor code; a future sync may re-open it.`
          }
          confirmText={confirm.type === 'resolve' ? 'Assign code' : 'Dismiss'}
          onConfirm={confirm.type === 'resolve' ? handleResolve : handleDismiss}
          onClose={() => setConfirm(null)}
          loading={busy}
          variant={confirm.type === 'dismiss' ? 'warning' : 'info'}
        />
      )}

      {assignTarget && (
        <AssignErpCodeModal
          vendor={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={(result) => {
            setAssignTarget(null);
            success(`Assigned ${result.assigned.join(', ')} — vendor now holds ${result.allCodes.join(', ')}`);
            fetchData();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={syncConfirm}
        title="Sync with ERP"
        message="This will synchronize vendor codes from the ERP database for all vendors without a code. It only fills blanks — existing codes are never overwritten. Continue?"
        confirmText="Sync Now"
        onConfirm={runSyncERP}
        onClose={() => setSyncConfirm(false)}
        variant="info"
      />
    </div>
  );
};

export default VendorCodeConflicts;
